import React from 'react';
import {
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Typography,
    Box,
} from '@mui/material';
import { Translate } from 'react-localize-redux';

interface ProductivityStats {
    totalDoctors: number;
    totalAppointments: number;
    totalUniquePatients: number;
    totalOutp001?: number;
    totalOutp002?: number;
    totalOutp003?: number;
    totalOutp004?: number;
}

interface DoctorData {
    researcher_uuid: string;
    doctor_name: string;
    doctor_surnames: string;
    department_name: string;
    total_appointments: number;
    unique_patients: number;
    outp001: number;
    outp002: number;
    outp003: number;
    outp004: number;
}

interface ProductivityTableProps {
    stats: ProductivityStats;
    data: DoctorData[];
    department: string;
    /** @deprecated Free consultations removed (SHER-561); kept for ChartFactory call sites */
    showOptionalColumns?: boolean;
    title?: string;
    type?: string;
}

const StatsCard = ({ title, value }: { title: React.ReactNode; value: number }) => (
    <Card className="h-full">
        <CardContent>
            <Typography color="textSecondary" gutterBottom>
                {title}
            </Typography>
            <Typography variant="h5" component="div">
                {value}
            </Typography>
        </CardContent>
    </Card>
);

const ProductivityTable = ({
    stats,
    data = [],
}: ProductivityTableProps) => {
    const rows = Array.isArray(data) ? data : [];
    const totals = {
        total_appointments: rows.reduce((sum, row) => sum + (row.total_appointments || 0), 0),
        unique_patients: rows.reduce((sum, row) => sum + (row.unique_patients || 0), 0),
        outp001: rows.reduce((sum, row) => sum + (row.outp001 || 0), 0),
        outp002: rows.reduce((sum, row) => sum + (row.outp002 || 0), 0),
        outp003: rows.reduce((sum, row) => sum + (row.outp003 || 0), 0),
        outp004: rows.reduce((sum, row) => sum + (row.outp004 || 0), 0),
    };

    return (
        <div className="space-y-2">
            <Box sx={{ mb: 1 }}>
                <Typography variant="h6">
                    <Translate id="hospital.analytics.clinical_activity.section_title" />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    <Translate id="hospital.analytics.clinical_activity.section_subtitle" />
                </Typography>
            </Box>

            <Grid container spacing={2} className="mb-4">
                <Grid item xs={12} sm={6} md={4}>
                    <StatsCard
                        title={<Translate id="hospital.analytics.clinical_activity.professionals" />}
                        value={stats?.totalDoctors || 0}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatsCard
                        title={<Translate id="hospital.analytics.clinical_activity.honored" />}
                        value={stats?.totalAppointments || 0}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <StatsCard
                        title={<Translate id="hospital.analytics.clinical_activity.unique_patients" />}
                        value={stats?.totalUniquePatients || 0}
                    />
                </Grid>
            </Grid>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.professional" />
                                </strong>
                            </TableCell>
                            <TableCell>
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.service" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.honored" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.unique_patients" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.outp001" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.outp002" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.outp003" />
                                </strong>
                            </TableCell>
                            <TableCell align="right">
                                <strong>
                                    <Translate id="hospital.analytics.clinical_activity.outp004" />
                                </strong>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow
                                key={`${row.researcher_uuid}-${row.department_name}`}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                                <TableCell component="th" scope="row">
                                    {`${row.doctor_name || ''} ${row.doctor_surnames || ''}`.trim()}
                                </TableCell>
                                <TableCell>{row.department_name}</TableCell>
                                <TableCell align="right">{row.total_appointments}</TableCell>
                                <TableCell align="right">{row.unique_patients}</TableCell>
                                <TableCell align="right">{row.outp001 || 0}</TableCell>
                                <TableCell align="right">{row.outp002 || 0}</TableCell>
                                <TableCell align="right">{row.outp003 || 0}</TableCell>
                                <TableCell align="right">{row.outp004 || 0}</TableCell>
                            </TableRow>
                        ))}
                        {rows.length > 0 && (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <strong>
                                        <Translate id="hospital.analytics.clinical_activity.total" />
                                    </strong>
                                </TableCell>
                                <TableCell align="right"><strong>{totals.total_appointments}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.unique_patients}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.outp001}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.outp002}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.outp003}</strong></TableCell>
                                <TableCell align="right"><strong>{totals.outp004}</strong></TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ProductivityTable;
